import { useQuery } from "@apollo/client";
import { useCallback } from "react";

import type { OnboardingDerivedStateQuery } from "@medbillai/generated-graphql-types";

import { errorHandling } from "../../../lib/apollo/utils";
import type { OnboardingStateData } from "../deriveInitial";
import type { OnboardingIdentity } from "../types";
import { onboardingStateQuery } from "./queries";

// Canonical UserMemory keys the FA prescreen writes (see checkFaEligibility).
const HOUSEHOLD_SIZE_MEMORY_KEY = "user.profile.householdSize";
const HOUSEHOLD_INCOME_MEMORY_KEY = "user.profile.householdIncome";
// Written by recordInsuranceStatus — the Insurance step's "I'm uninsured"
// answer (and the case checklist's equivalent).
const INSURANCE_STATUS_MEMORY_KEY = "user.profile.insuranceStatus";

const safeJsonParse = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/**
 * `projection__UserMemory.entries` is a JSON scalar map keyed by memoryType.
 * Codegen types it as `string` but the wire delivers the parsed object, so
 * accept both and treat anything else as "no entries".
 */
const memoryEntriesOf = (rawEntries: unknown): object | null => {
  const entries =
    typeof rawEntries === "string" ? safeJsonParse(rawEntries) : rawEntries;
  if (
    typeof entries !== "object" ||
    entries === null ||
    Array.isArray(entries)
  ) {
    return null;
  }
  return entries;
};

// Key presence, not a held value: these answer "has the user been through
// onboarding", and a memory the user later asked us to forget still attests
// that they were asked and answered. The `MemorySlot` remodel is what makes
// that readable here — forgetting now leaves the key carrying a `Forgotten`
// tombstone instead of removing it, so the key set is everything ever
// answered. Narrowing these to entries still held would send a user who
// forgot one answer back through a flow they have already completed.
const containsFaAnswers = (rawEntries: unknown): boolean => {
  const entries = memoryEntriesOf(rawEntries);
  return (
    entries !== null &&
    HOUSEHOLD_SIZE_MEMORY_KEY in entries &&
    HOUSEHOLD_INCOME_MEMORY_KEY in entries
  );
};

const containsInsuranceStatus = (rawEntries: unknown): boolean => {
  const entries = memoryEntriesOf(rawEntries);
  return entries !== null && INSURANCE_STATUS_MEMORY_KEY in entries;
};

/**
 * All three or nothing — a partial Member row enrolls the user and is then
 * skipped on every eligibility check.
 */
const identityOf = (
  persons: NonNullable<
    NonNullable<OnboardingDerivedStateQuery["me"]>["persons"]
  >,
): OnboardingIdentity | null => {
  const person = persons.find(p => p.primary);
  const firstName = (person?.givenNames ?? []).join(" ").trim();
  const lastName = person?.familyName?.trim() ?? "";
  const dateOfBirth = person?.dateOfBirth ?? "";
  return firstName !== "" && lastName !== "" && dateOfBirth !== ""
    ? { firstName, lastName, dateOfBirth }
    : null;
};

/**
 * `unavailable` is a read that failed — query error or no data. It is
 * deliberately not an empty state: "no partner bound" is a fact only a
 * successful read establishes, and the flow must not be entered without it;
 * `retry` refires the read.
 */
export type OnboardingStateResult =
  | { tag: "loading" }
  | { tag: "loaded"; state: OnboardingStateData }
  | { tag: "unavailable"; retry: () => void };

const LOADING: OnboardingStateResult = { tag: "loading" };

/**
 * Reads the presence flags that drive resume derivation. Assumes an
 * authenticated caller — mount only behind the auth gate.
 */
export const useOnboardingState = (): OnboardingStateResult => {
  const { data, loading, error, refetch } = useQuery(onboardingStateQuery, {
    // Resume must reflect canonical truth, not a cached snapshot from
    // earlier in the session.
    fetchPolicy: "network-only",
    // A retry must read as `loading`, not as the stale failure.
    notifyOnNetworkStatusChange: true,
    context: errorHandling("caller"),
  });

  const retry = useCallback(() => {
    // refetch rejects on failure; the error resurfaces through the hook state.
    refetch().catch(() => {});
  }, [refetch]);

  if (loading) {
    return LOADING;
  }
  if (error !== undefined || data === undefined) {
    return { tag: "unavailable", retry };
  }
  return {
    tag: "loaded",
    state: {
      // The primary (self) person only — a dependent-only account (e.g. from
      // a bill flow) must still pass through Complete profile.
      hasIdentity: (data.me?.persons ?? []).some(person => person.primary),
      identity: identityOf(data.me?.persons ?? []),
      // Answered, not insured: segmentation's `hasInsurance` only flips on a
      // carrier selection, so an uninsured declaration (recorded in user
      // memory) must also satisfy the resume gate or reload reopens the step.
      hasInsuranceAnswer:
        (data.me?.segmentationData?.hasInsurance ?? false) ||
        (data.projection__UserMemory ?? []).some(memory =>
          containsInsuranceStatus(memory.entries),
        ),
      features: data.me?.segmentationData?.features ?? null,
      hasFaAnswers: (data.projection__UserMemory ?? []).some(memory =>
        containsFaAnswers(memory.entries),
      ),
      partner: data.viewerPartner ?? null,
      // Normalized so `null` reads as "read, never accepted" rather than
      // "unread" — `deriveInitial` acts only on the former.
      acceptedTermsAt: data.me?.acceptedTermsAt ?? null,
    },
  };
};
